import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  changeQuestPackStatusAPI,
  createQuestPackAPI,
  deleteQuestPackAPI,
  fetchQuestPackListAPI,
  updateQuestPackAPI,
} from "../apis/questPack.api.js";
import { errorToastify, successToastify } from "../utils/toastify.util.js";
import { getApiErrorMessage } from "../utils/upload.util.js";

const initialState = {
  isLoad: false,
  isBtnLoad: false,
  isStatusLoad: false,
  isRedirect: false,
  listData: [],
  totalCount: 0,
  lastQuery: {},
  selectedQuestPack: null,
  deleteQuestPackId: null,
};

export const fetchQuestPackList = createAsyncThunk(
  "questPack/fetchList",
  async (payload, thunkAPI) => {
    try {
      const response = await fetchQuestPackListAPI(payload);
      return response;
    } catch (err) {
      const data = err?.response?.data || {
        success: false,
        message: getApiErrorMessage(err),
      };
      return thunkAPI.rejectWithValue(data);
    }
  },
);

export const createQuestPack = createAsyncThunk(
  "questPack/create",
  async (payload, thunkAPI) => {
    try {
      const response = await createQuestPackAPI(payload);
      const lastQuery = thunkAPI.getState()?.questPack?.lastQuery || {};
      thunkAPI.dispatch(fetchQuestPackList(lastQuery));
      return response;
    } catch (err) {
      const data = err?.response?.data || {
        success: false,
        message: getApiErrorMessage(err),
      };
      return thunkAPI.rejectWithValue(data);
    }
  },
);

export const updateQuestPack = createAsyncThunk(
  "questPack/update",
  async (payload, thunkAPI) => {
    try {
      const response = await updateQuestPackAPI(payload);
      const lastQuery = thunkAPI.getState()?.questPack?.lastQuery || {};
      thunkAPI.dispatch(fetchQuestPackList(lastQuery));
      return response;
    } catch (err) {
      const data = err?.response?.data || {
        success: false,
        message: getApiErrorMessage(err),
      };
      return thunkAPI.rejectWithValue(data);
    }
  },
);

export const changeQuestPackStatus = createAsyncThunk(
  "questPack/changeStatus",
  async (payload, thunkAPI) => {
    try {
      const response = await changeQuestPackStatusAPI(payload);
      const lastQuery = thunkAPI.getState()?.questPack?.lastQuery || {};
      thunkAPI.dispatch(fetchQuestPackList(lastQuery));
      return response;
    } catch (err) {
      const data = err?.response?.data || {
        success: false,
        message: getApiErrorMessage(err),
      };
      return thunkAPI.rejectWithValue(data);
    }
  },
);

export const deleteQuestPack = createAsyncThunk(
  "questPack/delete",
  async (payload, thunkAPI) => {
    try {
      const response = await deleteQuestPackAPI(payload);
      const lastQuery = thunkAPI.getState()?.questPack?.lastQuery || {};
      thunkAPI.dispatch(fetchQuestPackList(lastQuery));
      return response;
    } catch (err) {
      const data = err?.response?.data || {
        success: false,
        message: getApiErrorMessage(err),
      };
      return thunkAPI.rejectWithValue(data);
    }
  },
);

const questPackSlice = createSlice({
  name: "questPack",
  initialState,
  reducers: {
    setQuestPackRedirect: (state, { payload }) => {
      state.isRedirect = payload;
    },
    setSelectedQuestPack: (state, { payload }) => {
      state.selectedQuestPack = payload;
    },
    setDeleteQuestPackId: (state, { payload }) => {
      state.deleteQuestPackId = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchQuestPackList.pending, (state, action) => {
      state.isLoad = true;
      state.lastQuery = action.meta?.arg || {};
    });
    builder.addCase(fetchQuestPackList.fulfilled, (state, action) => {
      state.isLoad = false;
      if (action.payload?.success) {
        state.listData = action.payload?.result?.list || [];
        state.totalCount = Number(action.payload?.result?.totalCount || 0);
      } else {
        errorToastify(action.payload?.message || "Failed to fetch quest packs");
      }
    });
    builder.addCase(fetchQuestPackList.rejected, (state, action) => {
      state.isLoad = false;
      errorToastify(action.payload?.message || "Request is rejected.");
    });

    builder.addCase(createQuestPack.pending, (state) => {
      state.isBtnLoad = true;
    });
    builder.addCase(createQuestPack.fulfilled, (state, action) => {
      state.isBtnLoad = false;
      if (action.payload?.success) {
        state.isRedirect = true;
        const resultStatus = Number(action.payload?.result?.status);
        const savedAsDraft =
          !Number.isNaN(resultStatus) && action.payload?.result?.status != null
            ? resultStatus === 3
            : Number(action.meta?.arg?.status) === 3;
        successToastify(
          savedAsDraft
            ? "Quest pack saved as draft"
            : "Quest pack published to store",
        );
      } else {
        errorToastify(action.payload?.message || "Failed to create quest pack");
      }
    });
    builder.addCase(createQuestPack.rejected, (state, action) => {
      state.isBtnLoad = false;
      errorToastify(action.payload?.message || "Request is rejected.");
    });

    builder.addCase(updateQuestPack.pending, (state) => {
      state.isBtnLoad = true;
    });
    builder.addCase(updateQuestPack.fulfilled, (state, action) => {
      state.isBtnLoad = false;
      if (action.payload?.success) {
        state.isRedirect = true;
        const savedAsDraft = Number(action.meta?.arg?.status) === 3;
        successToastify(
          savedAsDraft
            ? "Quest pack saved as draft"
            : "Quest pack published to store",
        );
      } else {
        errorToastify(action.payload?.message || "Failed to update quest pack");
      }
    });
    builder.addCase(updateQuestPack.rejected, (state, action) => {
      state.isBtnLoad = false;
      errorToastify(action.payload?.message || "Request is rejected.");
    });

    builder.addCase(changeQuestPackStatus.pending, (state) => {
      state.isStatusLoad = true;
    });
    builder.addCase(changeQuestPackStatus.fulfilled, (state, action) => {
      state.isStatusLoad = false;
      if (action.payload?.success) {
        successToastify("Quest Pack status updated successfully");
      } else {
        errorToastify(action.payload?.message || "Failed to update quest pack status");
      }
    });
    builder.addCase(changeQuestPackStatus.rejected, (state, action) => {
      state.isStatusLoad = false;
      errorToastify(action.payload?.message || "Request is rejected.");
    });

    builder.addCase(deleteQuestPack.pending, (state) => {
      state.isBtnLoad = true;
    });
    builder.addCase(deleteQuestPack.fulfilled, (state, action) => {
      state.isBtnLoad = false;
      if (action.payload?.success) {
        state.deleteQuestPackId = null;
        successToastify("Quest Pack deleted successfully");
      } else {
        errorToastify(action.payload?.message || "Failed to delete quest pack");
      }
    });
    builder.addCase(deleteQuestPack.rejected, (state, action) => {
      state.isBtnLoad = false;
      errorToastify(action.payload?.message || "Request is rejected.");
    });
  },
});

export const {
  setQuestPackRedirect,
  setSelectedQuestPack,
  setDeleteQuestPackId,
} = questPackSlice.actions;

export default questPackSlice.reducer;
